import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import Button from 'aquilla/core/molecules/Button';
import React, { forwardRef, useState } from 'react';
import Image from '@base/FutureImage';
import { getFirstLetterForIcon } from '@cont/SeeAll/utils';
import { getIcon } from '@cont/NewPreSubscribed/utils';
import pushRoute from '@utils/pushRoute';
import { useIsMobile } from '@stores/AppCommonData';
import Link from 'aquilla/core/molecules/Link/Link';
import RightChevron from 'aquilla/icons/actions/RightChevron';
import { useRouter } from 'next/router';
import { getFullDateWithDayAtStart } from '@utils/dates';
import { localStorageAvailable } from '@utils/localStorage';
import { segment } from '@actions/analytics';
import segmentStorage from '@events/segmentStorage';
import {
  BLOCK_TYPE,
  DELIVERY_STATUS,
  BlockTypeToLabel,
  contentBlockType,
  BUNDLE_SEE_ALL_CLICKED,
  NOTES_TOPIC_GROUP_SELECTED
} from './constants';
import PhysicalNotesModal from './PhysicalNotesModal';
import TestCard from './TestCard';

const Container = styled.div`
  margin-bottom: ${Spacings.SPACING_14B};
  ${mediaQuery.uptoMobile} {
    margin-bottom: 0;
    border-bottom: 1px solid var(--color-i-divider);
  }
`;

const Title = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_0B};
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_0B};
    padding-bottom: ${Spacings.SPACING_0B};
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;

const StyledButton = styled(Button)`
  width: 222px;
  height: ${Spacings.SPACING_12B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-bottom: ${Spacings.SPACING_3B};
  }
`;

const ImageContainer = styled.div`
  flex: 0 0 78px;
  height: 110px;
`;

const ContentContainer = styled.div`
  margin-left: ${Spacings.SPACING_4B};
`;

const Heading = styled(Typography)``;

const SubHeading = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_6B};
  margin-top: ${Spacings.SPACING_3B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_0B};
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;

const BulletList = styled.ul`
  margin-top: ${Spacings.SPACING_2B};
  padding-left: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_6};
  }
`;

const BulletListItem = styled.li`
  font-size: 14px;
  line-height: 20px;
  color: var(--color-text-secondary);
`;

const InfoContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${Spacings.SPACING_6B};
  margin-top: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_3B};
    margin-bottom: ${Spacings.SPACING_3B};
  }
`;

const AdditionalInfo = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_3B};
  }
`;

const ViewInAppInfo = styled.div`
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    display: flex;
    margin-bottom: ${Spacings.SPACING_3B};
    align-items: center;
    gap: ${Spacings.SPACING_1B};
    text-align: center;
    justify-content: center;
  }
`;

const CopyrightInfo = styled(Typography)`
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_3B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_0B};
    margin-bottom: ${Spacings.SPACING_3B};
  }
`;

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  height: fit-content;
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_4B};
  cursor: pointer;
  width: 376px;
  ${mediaQuery.uptoMobile} {
    align-items: center;
    border: 0px;
    padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_0B};
  }
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-self: center;
`;

const StyledP1 = styled(Typography)`
  font-weight: ${Fonts.SEMIBOLD};
`;

const ImageWrapper = styled.div`
  margin-right: ${Spacings.SPACING_4B};
  width: ${Spacings.SPACING_10B};
  height: ${Spacings.SPACING_10B};

  & > svg {
    width: ${Spacings.SPACING_10B};
    height: ${Spacings.SPACING_10B};
  }
`;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 376px 376px;
  grid-gap: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_6B};

  ${mediaQuery.uptoMobile} {
    grid-template-columns: 1fr;
    grid-gap: ${Spacings.SPACING_0B};
    margin-bottom: ${Spacings.SPACING_3B};
  }
`;

const Arrow = styled.div`
  display: flex;
  opacity: 1;
  transition: 0.3s ease;
  margin-left: auto;
`;

const CTAWrapper = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_0B};
    flex-direction: column;
  }
`;

const topicGroupsToShow = 5;

const NotesInfo = forwardRef(
  ({ data, type, accessDuration, appDownloadLink, goalSegmentData }, ref) => {
    const [open, setOpen] = useState(false);
    const isMobile = useIsMobile();

    const {
      query: { noteUID }
    } = useRouter();

    const {
      title,
      valueOfferings,
      portraitThumbnail,
      uid,
      topicGroups,
      copyrightUrl,
      piracyPolicyUrl,
      goalUid,
      deliveryStatusDetails,
      accessTill,
      items: testSeriesCards
    } = data;

    const name = BlockTypeToLabel[type];

    const handleOnClose = () => {
      setOpen(false);
    };

    const handleClick = () => {
      if (localStorageAvailable()) {
        const notesSegmentData = JSON.parse(
          localStorage.getItem('notesSegmentData')
        );
        const lastPrimarySource = segmentStorage.getItem('lps');
        segment.track(BUNDLE_SEE_ALL_CLICKED, {
          ...goalSegmentData,
          ...notesSegmentData,
          entity_content_subtype: contentBlockType[type],
          last_primary_source: lastPrimarySource
        });
      }
      if (type === BLOCK_TYPE.PHYSICAL) {
        setOpen(true);
      }
      if (type === BLOCK_TYPE.DIGITAL) {
        if (localStorageAvailable()) {
          const notesSegmentData = JSON.parse(
            localStorage.getItem('notesSegmentData')
          );
          const lastPrimarySource = segmentStorage.getItem('lps');
          segment.track(NOTES_TOPIC_GROUP_SELECTED, {
            ...goalSegmentData,
            ...notesSegmentData,
            entity_content_subtype: contentBlockType[type],
            last_primary_source: lastPrimarySource
          });
        }
        return isMobile
          ? window.open(appDownloadLink, '_blank')
          : pushRoute(
              '/goal/[goalSlug]/[goalUID]/store/notes',
              `/goal/-/${goalUid}/store/notes`,
              {
                shallow: true
              }
            );
      }
      if (type === BLOCK_TYPE.TEST_SERIES) {
        pushRoute(
          '/store/test-series/[slug]/[courseUID]',
          `/store/test-series/-/${uid}`,
          { shallow: true }
        );
      }
    };

    const handleDeliveryStatusClick = () => {
      const { value } = deliveryStatusDetails || {};
      if (value === DELIVERY_STATUS.ADD_ADDRESS) {
        return pushRoute(
          `/goal/[goalSlug]/[goalUID]/setup-address?storeBundleUid=${noteUID}`,
          `/goal/-/${goalUid}/setup-address?storeBundleUid=${noteUID}`
        );
      }
      if (value === DELIVERY_STATUS.TRACK_ORDER) {
        if (isMobile) {
          return window.open(appDownloadLink, '_blank');
        }
        return pushRoute(
          `/store/notes/[slug]/tracking-flow?storeBundleUid=${noteUID}`,
          `/store/notes/${goalUid}/tracking-flow?storeBundleUid=${noteUID}`
        );
      }
      if (value === DELIVERY_STATUS.VIEW_ORDER_DETAILS) {
        if (isMobile) {
          return window.open(appDownloadLink, '_blank');
        }
        return pushRoute(
          `/store/notes/[slug]/tracking-flow?storeBundleUid=${noteUID}`,
          `/store/notes/${goalUid}/tracking-flow?storeBundleUid=${noteUID}`
        );
      }
    };

    const getButtonLabel = () => {
      if (type === BLOCK_TYPE.PHYSICAL) {
        return 'See all details';
      }
      if (type === BLOCK_TYPE.DIGITAL) {
        return !isMobile
          ? `See all ${topicGroups.length} subjects`
          : 'View in app';
      }
      if (type === BLOCK_TYPE.TEST_SERIES) {
        return 'See test series';
      }
    };

    const getAccessLabel = () => {
      if (accessTill)
        return `Access available till ${getFullDateWithDayAtStart(accessTill)}`;

      if (accessDuration)
        return `Access available for ${accessDuration} post purchase`;

      return '';
    };

    const renderTopicGroup = () => {
      if (!topicGroups) return;

      return topicGroups.slice(0, topicGroupsToShow)?.map((topic) => {
        const letter = getFirstLetterForIcon(topic.name);

        return (
          <FlexRow key={topic.uid} onClick={handleClick}>
            <ImageWrapper>{getIcon(letter)}</ImageWrapper>
            <FlexColumn>
              <StyledP1 variant="p1">{topic.name}</StyledP1>
              <Typography variant="p3" color="var(--color-text-secondary)">
                {topic.childrenCount !== 0 &&
                  (topic.childrenCount > 1
                    ? `${topic.childrenCount} topics`
                    : `${topic.childrenCount} topic`)}
              </Typography>
            </FlexColumn>
            {isMobile && (
              <Arrow>
                <RightChevron />
              </Arrow>
            )}
          </FlexRow>
        );
      });
    };

    return (
      <Container ref={ref}>
        <Title variant={isMobile ? 'h5' : 'h4'}>{name}</Title>

        {type === BLOCK_TYPE.TEST_SERIES &&
        !!testSeriesCards &&
        testSeriesCards.length > 0 ? (
          <>
            <SubHeading
              variant={isMobile ? 'p3' : 'p2'}
              color="var(--color-text-secondary)"
            >
              {title}
            </SubHeading>
            {testSeriesCards.map((cardInfo) => {
              return (
                <TestCard
                  className="schedule-card-ele"
                  key={cardInfo.uid}
                  testInfo={cardInfo}
                />
              );
            })}
          </>
        ) : (
          <InfoContainer>
            <ImageContainer>
              <Image
                alt={name}
                src={portraitThumbnail || 'https://via.placeholder.com/78x110'}
                width={78}
                height={110}
              />
            </ImageContainer>
            <ContentContainer>
              <Heading variant={isMobile ? 'h6' : 'h5'}>{title}</Heading>
              <BulletList>
                {valueOfferings?.map((point, index) => (
                  <BulletListItem key={String(index)}>
                    <Typography
                      variant={isMobile ? 'p3' : 'p2'}
                      color="var(--color-text-secondary)"
                    >
                      {point}
                    </Typography>
                  </BulletListItem>
                ))}
              </BulletList>
            </ContentContainer>
          </InfoContainer>
        )}

        <AdditionalInfo variant="p2">
          {type !== BLOCK_TYPE.PHYSICAL
            ? getAccessLabel()
            : deliveryStatusDetails?.title ||
              'Delivery to your home will take 12-15 days'}
        </AdditionalInfo>

        {type === BLOCK_TYPE.DIGITAL && !!topicGroups && (
          <Wrapper>{renderTopicGroup()}</Wrapper>
        )}

        {isMobile && type === BLOCK_TYPE.DIGITAL && (
          <ViewInAppInfo>
            <Image
              src="store/notes/mobile.svg"
              height={16}
              width={16}
              alt="mobile"
            />
            <Typography variant="p3" color="var(--color-text-secondary)">
              PDF notes are available only on Android and iOS app
            </Typography>
          </ViewInAppInfo>
        )}

        <CTAWrapper>
          {(type !== BLOCK_TYPE.DIGITAL ||
            topicGroupsToShow < topicGroups.length ||
            isMobile) && (
            <StyledButton
              label={getButtonLabel()}
              variant="secondary"
              size="large"
              onClick={handleClick}
            />
          )}

          {type === BLOCK_TYPE.PHYSICAL && deliveryStatusDetails?.ctaLabel && (
            <StyledButton
              label={deliveryStatusDetails?.ctaLabel}
              variant="primary"
              size="large"
              onClick={handleDeliveryStatusClick}
            />
          )}
        </CTAWrapper>

        {type === BLOCK_TYPE.DIGITAL && (
          <CopyrightInfo variant="p2">
            Read more about <Link href={copyrightUrl}>copyright</Link> and{' '}
            <Link href={piracyPolicyUrl}>piracy policy</Link>
          </CopyrightInfo>
        )}
        <PhysicalNotesModal
          data={data}
          open={open}
          handleOnClose={handleOnClose}
        />
      </Container>
    );
  }
);

NotesInfo.displayName = 'Notes Info';

export default React.memo(NotesInfo);
